import { userPreferencesApi } from './api.js';
import { authStore } from './auth.js';
import { themeId, colorMode, inwdDetails } from './theme.js';
import { locale } from '../i18n/index.js';

/**
 * Bridges the appearance stores to the account.
 *
 * Theme, colour mode and locale are each owned by their own store, which
 * writes through to localStorage. That copy stays: it is what the pre-paint
 * bootstrap in `index.html` reads, and it is the only thing available on the
 * login screen, where there is no session to ask. What this module adds is the
 * account: on sign-in the server's document wins, and every later change is
 * pushed back so it follows the user to their next device.
 *
 * Server-wins is deliberate. The alternative — whatever this browser last
 * showed — means a fresh profile silently overwrites a deliberate choice made
 * somewhere else, and the last device you signed in from decides for all the
 * others.
 *
 * Kept out of `theme.js` on purpose: that store must stay usable with no
 * session and no network, and giving it a dependency on the API client and the
 * auth state would couple the pre-paint path to both.
 *
 * The dependency runs one way — preferences depends on auth and on the API
 * client, never the reverse. Having `auth.js` reach back here for the sign-out
 * reset would close a cycle through `api.js`, which imports `auth.js`.
 */

/** True once the server's document has been applied for this session. */
let hydrated = false;

/** Suppresses the push while we are applying values that came *from* the server. */
let applying = false;

/**
 * The signed-in session the stores currently belong to.
 *
 * Bumped on sign-out and whenever a different user signs in. A load carries
 * the generation it started under and is thrown away if that is no longer the
 * current one: a slow answer for the previous account must never be applied
 * to — or pushed up as — the next one.
 */
let generation = 0;
let sessionUser = null;

/** The load running for the current generation, shared by overlapping callers. */
let inFlight = null;

function push(patch) {
  // Before hydration the stores hold whatever localStorage had, which is not
  // the user's account preference and must not be written over it. The
  // subscriptions below also fire once on registration; this is what keeps
  // that initial emission from becoming a write.
  if (!hydrated || applying) return;

  userPreferencesApi.patch(patch).catch((error) => {
    // A preference that fails to save is not worth interrupting anyone for:
    // the change is already applied locally and cached, and the next change
    // will try again. Losing it silently would be worse than the noise. That
    // includes a 400 from a server older than this interface, which does not
    // know a key yet (`inwdDetails`): the local value stays in force.
    console.warn('[preferences] could not save', Object.keys(patch).join(', '), error);
  });
}

themeId.subscribe((value) => push({ theme: value }));
colorMode.subscribe((value) => push({ colorMode: value }));
inwdDetails.subscribe((value) => push({ inwdDetails: value }));
locale.subscribe((value) => push({ locale: value }));

/**
 * Fetch the signed-in user's preferences and apply them.
 *
 * Every key is optional: a user who has never saved gets an empty document,
 * and each absent key leaves the local value in place. That is what makes the
 * first sign-in on a new account keep the theme the person was already looking
 * at rather than snapping back to the default.
 */
export function loadPreferences() {
  // App.svelte drives this from a reactive block, which re-runs on any auth
  // state change; hydrating once per session is enough.
  if (hydrated) return Promise.resolve(null);
  if (inFlight?.generation === generation) return inFlight.promise;

  const mine = generation;
  const promise = fetchAndApply(mine).finally(() => {
    if (inFlight?.generation === mine) inFlight = null;
  });
  inFlight = { generation: mine, promise };
  return promise;
}

async function fetchAndApply(mine) {
  try {
    const { preferences = {} } = await userPreferencesApi.get();
    if (mine !== generation) {
      // Signed out (or someone else signed in) while this was on the wire.
      return null;
    }

    applying = true;
    if (preferences.theme) themeId.set(preferences.theme);
    if (preferences.colorMode) colorMode.set(preferences.colorMode);
    if (typeof preferences.inwdDetails === 'boolean') inwdDetails.set(preferences.inwdDetails);
    if (preferences.locale) locale.set(preferences.locale);
    applying = false;

    hydrated = true;
    return preferences;
  } catch (error) {
    applying = false;
    // Without the server's answer we cannot tell a deliberate preference from
    // a stale cache, so we stay unhydrated: the local values keep driving the
    // UI and nothing is pushed up on top of whatever the account holds.
    console.warn('[preferences] could not load; keeping the local values', error);
    return null;
  }
}

/**
 * Sign-out, or a different user signing in, starts a new generation: the
 * hydration is forgotten, so the next user in this tab does not have the
 * previous one's cached stores pushed up to their account, and any load still
 * running for the previous session is discarded when it answers.
 */
authStore.subscribe((state) => {
  if (state.loading) return;
  const user = state.isAuthenticated ? (state.user?.id ?? null) : null;
  if (!state.isAuthenticated || user !== sessionUser) {
    generation += 1;
    hydrated = false;
    applying = false;
    inFlight = null;
  }
  sessionUser = user;
});
